import React, { useEffect, useState, useRef } from 'react';
import useGameBase from '../../hooks/useGameBase';

function ChoiceButton({ label, onClick, disabled, className }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 rounded shadow transform transition-transform duration-150 ease-out ${className} ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}`}
    >
      {label}
    </button>
  );
}

function RockPaperScissorsGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }){
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const mySubmission = gameState.submissions && gameState.submissions[user.socketId];
  const revealed = gameState.revealed;
  const pickDeadline = gameState.pickDeadline || null;
  const roundDefeats = React.useMemo(() => (gameState.roundDefeats || {}), [gameState.roundDefeats]);

  // animation state: list of [winner, defeated] entries flattened
  const [animQueue, setAnimQueue] = useState([]);
  const [animIndex, setAnimIndex] = useState(0);
  const animTimerRef = useRef(null);

  useEffect(()=>{
    if (!revealed) {
      setAnimQueue([]); setAnimIndex(0);
      if (animTimerRef.current) { clearInterval(animTimerRef.current); animTimerRef.current = null; }
      return;
    }
    // build queue: for each winner, for each defeated opponent
    const queue = [];
    Object.keys(roundDefeats).forEach(w => {
      (roundDefeats[w]||[]).forEach(def => queue.push({ winner: w, defeated: def }));
    });
    setAnimQueue(queue);
    setAnimIndex(0);
    if (animTimerRef.current) clearInterval(animTimerRef.current);
    if (queue.length > 0) {
      // derive interval so the whole queue cycles approximately once during reveal
      // Prefer seconds-based config on gameState (revealSec/revealPerDefeatSec/pickSec) but
      // fall back to legacy ms fields if present.
      const revealSec = (gameState.revealSec != null) ? Number(gameState.revealSec) : (gameState.revealMs != null ? Number(gameState.revealMs) / 1000 : 3.5);
      const revealMs = Math.max(0, Math.floor(revealSec * 1000));
      const interval = Math.max(500, Math.floor(revealMs / queue.length));
      animTimerRef.current = setInterval(()=>{
        setAnimIndex(i => (i+1) % queue.length);
      }, interval);
    }
    return ()=>{ if (animTimerRef.current) { clearInterval(animTimerRef.current); animTimerRef.current = null; } };
  }, [revealed, roundDefeats, gameState.revealSec, gameState.revealMs]);

  const submit = (choice) => submitAction({ type: 'submit', choice }).catch(()=>{});

  // countdown for picks
  const [remainingMs, setRemainingMs] = useState(null);
  const countdownRef = useRef(null);
  useEffect(()=>{
    if (!pickDeadline || revealed) { setRemainingMs(null); if (countdownRef.current){ clearInterval(countdownRef.current); countdownRef.current=null; } return; }
    const tick = ()=>{
      const rem = Math.max(0, pickDeadline - Date.now());
      setRemainingMs(rem);
      if (rem <= 0 && countdownRef.current){ clearInterval(countdownRef.current); countdownRef.current=null; }
    };
    tick();
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(tick, 200);
    return ()=>{ if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current=null; } };
  }, [pickDeadline, revealed]);

  // reveal countdown
  const revealDeadline = gameState.revealDeadline || null;
  const [revealRemainingMs, setRevealRemainingMs] = useState(null);
  const revealRef = useRef(null);
  useEffect(()=>{
    if (!revealDeadline || !revealed) { setRevealRemainingMs(null); if (revealRef.current){ clearInterval(revealRef.current); revealRef.current=null; } return; }
    const tick = ()=>{
      const rem = Math.max(0, revealDeadline - Date.now());
      setRevealRemainingMs(rem);
      if (rem <= 0 && revealRef.current){ clearInterval(revealRef.current); revealRef.current=null; }
    };
    tick();
    if (revealRef.current) clearInterval(revealRef.current);
    revealRef.current = setInterval(tick, 200);
    return ()=>{ if (revealRef.current) { clearInterval(revealRef.current); revealRef.current=null; } };
  }, [revealDeadline, revealed]);

  return (
    <div className="rps-game relative">
      <style>{`
        @keyframes slideInDown { from { transform: translateY(-20px); opacity:0 } to { transform: translateY(0); opacity:1 } }
        @keyframes pulseScore { 0% { transform: scale(1) } 50% { transform: scale(1.25) } 100% { transform: scale(1) } }
      `}</style>

      
      <div className="mt-2  text-gray-900 dark:text-gray-100 ">Round {gameState.currentRound} / {gameState.rounds}</div>
      { revealed && !gameState.finished && (
        <div className="mt-2 w-full">
          <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
            {(() => {
              // compute reveal ms preferring seconds fields
              const revealSec = (gameState.revealSec != null) ? Number(gameState.revealSec) : (gameState.revealMs != null ? Number(gameState.revealMs) / 1000 : 3.5);
              const revealMs = Math.max(0, Math.floor(revealSec * 1000));
              const remaining = (revealRemainingMs != null) ? revealRemainingMs : revealMs;
              const frac = revealMs > 0 ? Math.max(0, Math.min(1, 1 - (remaining / revealMs))) : 1;
              return <div className="h-full bg-yellow-400" style={{ width: `${Math.round(frac * 100)}%`, transition: 'width 0.1s linear' }} />;
            })()}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">Reveal ends in { Math.max(0, Math.ceil(((revealRemainingMs != null ? revealRemainingMs : ((gameState.revealSec != null) ? Number(gameState.revealSec) * 1000 : (gameState.revealMs || 3500))) )/1000)) }s</div>
        </div>
      )}

      <div className="mt-4">
        { remainingMs != null && !gameState.finished ? (
          <>
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded mt-1 overflow-hidden">
              <div
                className="h-full bg-yellow-400"
                style={{ width: `${(() => {
                  const pickSec = (gameState.pickSec != null) ? Number(gameState.pickSec) : (gameState.pickMs != null ? Number(gameState.pickMs) / 1000 : 5);
                  const pickMs = Math.max(0, Math.floor(pickSec * 1000));
                  return Math.max(0, Math.min(100, (100 * (1 - (remainingMs / (pickMs || 5000))))));
                })()}%`, transition: 'width 0.1s linear' }}
              />
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">{ Math.max(0, Math.ceil(remainingMs/1000)) }s left</div>
          </>
        ) : null }

        {!revealed && (
          <div className="mt-3 flex gap-3 justify-center">
            <ChoiceButton label="✊" title="Rock" onClick={()=>submit('rock')} disabled={!!mySubmission || (pickDeadline && Date.now() >= pickDeadline)} className={`bg-gradient-to-br from-red-400 to-red-500 dark:from-red-400 dark:to-red-500 text-5xl ${!!mySubmission ? '' : ''}`} />
            <ChoiceButton label="✋" title="Paper" onClick={()=>submit('paper')} disabled={!!mySubmission || (pickDeadline && Date.now() >= pickDeadline)} className={`bg-gradient-to-br from-green-400 to-green-500 dark:from-green-400 dark:to-green-500 text-5xl ${!!mySubmission ? '' : ''}`} />
            <ChoiceButton label="✌️" title="Scissors" onClick={()=>submit('scissors')} disabled={!!mySubmission || (pickDeadline && Date.now() >= pickDeadline)} className={`bg-gradient-to-br from-blue-400 to-blue-500 dark:from-blue-400 dark:to-blue-500 text-5xl ${!!mySubmission ? '' : ''}`} />
          </div>
        )}
      </div>

      <div className="mt-4  text-gray-900 dark:text-gray-100 ">
        <h4 className="font-semibold">Players</h4>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {gamePlayers.map(p => {
            const isDefeatedNow = animQueue[animIndex] && animQueue[animIndex].defeated === p.socketId;
            const isWinnerNow = animQueue[animIndex] && animQueue[animIndex].winner === p.socketId;
            return (
              <div key={p.socketId} className={`relative text-gray-900 dark:text-gray-100 p-2 rounded shadow-sm flex justify-between items-center overflow-hidden ${isDefeatedNow ? 'ring-2 ring-red-400' : ''} ${isWinnerNow ? 'ring-2 ring-green-400' : ''}`}>
                <div className="flex items-center gap-2">
                  <div className="font-semibold">{p.username}</div>
                  {isWinnerNow ? <div className="text-xs text-green-600">wins</div> : null}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-xs mr-2">{revealed ? (gameState.submissions && gameState.submissions[p.socketId]) : (gameState.submissions && gameState.submissions[p.socketId] ? 'Submitted' : '—')}</div>
                  {/* score pop placeholder: animated when score increases */}
                </div>
              </div>
            );
          })}
        </div>
      </div>

          
      {/* {animQueue.length > 0 && revealed && animQueue[animIndex] && (
        (() => {
          const entry = animQueue[animIndex];
          const winner = gamePlayers.find(p=>p.socketId===entry.winner);
          const defeated = gamePlayers.find(p=>p.socketId===entry.defeated);
          if (!winner || !defeated) return null;
          return (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-30">
              <div className="bg-black bg-opacity-30 text-white px-3 py-2 rounded flex items-center gap-3" style={{ animation: 'slideInDown 300ms ease-out' }}>
                <div className="font-bold">{winner.username} ({gameState.submissions[winner.socketId]})</div>
                <div className="text-yellow-200 dark:text-yellow-300"> beats </div>
                <div>{defeated.username} ({gameState.submissions[defeated.socketId]})</div>
              </div>
            </div>
          );
        })()
      )} */}
    </div>
  );
}

RockPaperScissorsGame.Guide = function RPSGuide(){
  return (
    <div>
      <h3 className="font-bold">Rock Paper Scissors</h3>
      <p>Each round all players choose either rock, paper or scissors simultaneously.</p>
      <p>Rock crushes scissors.</p>
      <p>Scissors cuts paper.</p>
      <p>Paper covers rock.</p>
      <p>Scoring is pairwise: you get 1 point for each opponent you beat this round.</p>
    </div>
  );
};

export default RockPaperScissorsGame;
